import React, { useEffect, useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import CustomButton from '../../components/ui/buttons/CustomButton';
import Input from '../../components/ui/inputs/Input';
import CurrencyInput from '../../components/ui/inputs/CurrencyInput';
import { fetchWithCsrf } from '../../utils/fetchWithCsrf';
import { useToast } from '../../components/ui/toast/ToastContext';

const INITIAL_STATE = {
  balancing: {
    unbalanced: { from: '1', to: '50' },
    mediumBalanced: { from: '51', to: '80' },
    balanced: { from: '81', to: '100' },
  },
  liquidity: {
    low: { from: '1', to: '100' },
    good: { from: '51', to: '80' },
    excess: { from: '81', to: '100' },
  },
  salesPlanningMinLossMargin: 500,
};

const settingsToForm = (settings) => ({
  balancing: {
    unbalanced: {
      from: String(settings.balancing_unbalanced_min ?? 1),
      to: String(settings.balancing_unbalanced_max ?? 100),
    },
    mediumBalanced: {
      from: String(settings.balancing_attention_min ?? 1),
      to: String(settings.balancing_attention_max ?? 100),
    },
    balanced: {
      from: String(settings.balancing_balanced_min ?? 1),
      to: String(settings.balancing_balanced_max ?? 100),
    },
  },
  liquidity: {
    low: {
      from: String(settings.liquidity_low_min ?? 1),
      to: String(settings.liquidity_low_max ?? 100),
    },
    good: {
      from: String(settings.liquidity_good_min ?? 1),
      to: String(settings.liquidity_good_max ?? 100),
    },
    excess: {
      from: String(settings.liquidity_excess_min ?? 1),
      to: String(settings.liquidity_excess_max ?? 100),
    },
  },
  salesPlanningMinLossMargin: Number(settings.sales_planning_min_loss_margin ?? 500),
});

const formToSettings = (form) => ({
  balancing_unbalanced_min: form.balancing.unbalanced.from,
  balancing_unbalanced_max: form.balancing.unbalanced.to,
  balancing_attention_min: form.balancing.mediumBalanced.from,
  balancing_attention_max: form.balancing.mediumBalanced.to,
  balancing_balanced_min: form.balancing.balanced.from,
  balancing_balanced_max: form.balancing.balanced.to,
  liquidity_low_min: form.liquidity.low.from,
  liquidity_low_max: form.liquidity.low.to,
  liquidity_good_min: form.liquidity.good.from,
  liquidity_good_max: form.liquidity.good.to,
  liquidity_excess_min: form.liquidity.excess.from,
  liquidity_excess_max: form.liquidity.excess.to,
  sales_planning_min_loss_margin: form.salesPlanningMinLossMargin,
});

const validateThresholdGroup = (groupName, ranges) => {
  const normalizedRanges = ranges.map((range) => ({
    ...range,
    fromNumber: Number(range.from),
    toNumber: Number(range.to),
  }));

  for (let i = 0; i < normalizedRanges.length; i += 1) {
    const range = normalizedRanges[i];

    if (range.from === '' || range.to === '') {
      return {
        valid: false,
        message: `${groupName}: preencha todos os campos antes de salvar.`,
      };
    }

    if (Number.isNaN(range.fromNumber) || Number.isNaN(range.toNumber)) {
      return {
        valid: false,
        message: `${groupName}: todos os limiares precisam ser números válidos.`,
      };
    }

    if (range.fromNumber < 1 || range.toNumber > 100) {
      return {
        valid: false,
        message: `${groupName}: os limiares precisam estar entre 1 e 100.`,
      };
    }

    if (range.fromNumber > range.toNumber) {
      return {
        valid: false,
        message: `${groupName}: em "${range.label}", o valor inicial não pode ser maior que o valor final.`,
      };
    }

    if (!Number.isInteger(range.fromNumber) || !Number.isInteger(range.toNumber)) {
      return {
        valid: false,
        message: `${groupName}: use apenas números inteiros nos limiares.`,
      };
    }
  }

  if (normalizedRanges[0].fromNumber !== 1) {
    return {
      valid: false,
      message: `${groupName}: a primeira faixa precisa começar em 1.`,
    };
  }

  for (let i = 1; i < normalizedRanges.length; i += 1) {
    const previousRange = normalizedRanges[i - 1];
    const currentRange = normalizedRanges[i];

    if (currentRange.fromNumber !== previousRange.toNumber + 1) {
      return {
        valid: false,
        message: `${groupName}: a faixa "${currentRange.label}" precisa começar em ${previousRange.toNumber + 1}.`,
      };
    }
  }

  const lastRange = normalizedRanges[normalizedRanges.length - 1];

  if (lastRange.toNumber !== 100) {
    return {
      valid: false,
      message: `${groupName}: a última faixa precisa terminar em 100.`,
    };
  }

  return { valid: true };
};

const validateFormThresholds = (form) => {
  const balancingValidation = validateThresholdGroup('Balanceamento', [
    {
      label: 'Não-balanceada',
      from: form.balancing.unbalanced.from,
      to: form.balancing.unbalanced.to,
    },
    {
      label: 'Médio-balanceada',
      from: form.balancing.mediumBalanced.from,
      to: form.balancing.mediumBalanced.to,
    },
    {
      label: 'Balanceada',
      from: form.balancing.balanced.from,
      to: form.balancing.balanced.to,
    },
  ]);

  if (!balancingValidation.valid) return balancingValidation;

  const liquidityValidation = validateThresholdGroup('Liquidez', [
    {
      label: 'Baixa liquidez',
      from: form.liquidity.low.from,
      to: form.liquidity.low.to,
    },
    {
      label: 'Boa liquidez',
      from: form.liquidity.good.from,
      to: form.liquidity.good.to,
    },
    {
      label: 'Excesso de liquidez',
      from: form.liquidity.excess.from,
      to: form.liquidity.excess.to,
    },
  ]);

  if (!liquidityValidation.valid) return liquidityValidation;

  if (
    form.salesPlanningMinLossMargin === '' ||
    Number.isNaN(Number(form.salesPlanningMinLossMargin)) ||
    Number(form.salesPlanningMinLossMargin) < 0
  ) {
    return {
      valid: false,
      message: 'Margem mínima de prejuízo: informe um valor válido, maior ou igual a zero.',
    };
  }

  return { valid: true };
};

const ThresholdCard = ({ title, values, onChange }) => (
  <div className="bg-blueLight rounded-2xl p-5 flex flex-col gap-4">
    <h3 className="text-base font-semibold text-blueDarker">{title}</h3>
    <div className="grid grid-cols-2 gap-4">
      <Input
        name="from"
        label="De (%)"
        type="number"
        step="1"
        min="1"
        max="100"
        value={values.from}
        onChange={(e) => onChange('from', e.target.value)}
      />
      <Input
        name="to"
        label="Até (%)"
        type="number"
        step="1"
        min="1"
        max="100"
        value={values.to}
        onChange={(e) => onChange('to', e.target.value)}
      />
    </div>
  </div>
);

const SystemSettings = () => {
  const [form, setForm] = useState(INITIAL_STATE);
  const [savedForm, setSavedForm] = useState(INITIAL_STATE);
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();

  useEffect(() => {
    fetch('/app/analyst/global_settings', {
      headers: { Accept: 'application/json' },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.settings) {
          const mapped = settingsToForm(data.settings);
          setForm(mapped);
          setSavedForm(mapped);
          addToast({ type: 'success', message: 'Configurações do sistema carregadas com sucesso.' });
        }
      })
      .catch(() => {
        addToast({ type: 'error', message: 'Erro ao carregar configurações do sistema.' });
      });
  }, []);

  useEffect(() => {
    fetch("/app/analyst/global_settings", {
      headers: { Accept: "application/json" },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.settings) {
          const mapped = settingsToForm(data.settings);
          setForm(mapped);
          setSavedForm(mapped);
        }
      })
      .catch(() => {});
  }, []);

  const handleChange = (section, key, field, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: {
          ...prev[section][key],
          [field]: value,
        },
      },
    }));
  };

  const handleCancel = () => {
    setForm(savedForm);
  };

  const handleSalesPlanningMinLossMarginChange = (value) => {
    setForm((prev) => ({
      ...prev,
      salesPlanningMinLossMargin: value,
    }));
  };

  const handleSave = () => {
    const validation = validateFormThresholds(form);

    if (!validation.valid) {
      addToast({
        type: 'error',
        message: validation.message,
      });

      return;
    }

    setLoading(true);

    fetchWithCsrf('/app/analyst/global_settings', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ settings: formToSettings(form) }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setSavedForm(form);

          addToast({
            type: 'success',
            message: 'Configurações do sistema salvas com sucesso.',
          });
        } else {
          addToast({
            type: 'error',
            message: data.error || 'Erro ao salvar configurações do sistema.',
          });
        }
      })
      .catch(() => {
        addToast({ type: 'error', message: 'Erro ao salvar configurações do sistema.' });
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="p-8 flex flex-col gap-6">
      {/* Page Title */}
      <div className="flex items-center gap-3">
        <SlidersHorizontal className="text-tealMedium" size={22} strokeWidth={1.5} />
        <h1 className="text-2xl font-semibold text-blueDarker">Configurações do sistema</h1>
      </div>

      {/* White container wrapping everything below the title */}
      <div className="bg-white rounded-custom20 p-8 flex flex-col gap-8">
        {/* Balancing Thresholds */}
        <section className="flex flex-col gap-4">
          <h2 className="text-base font-semibold text-blueDarker">Limiares de Notas de Balanceamento</h2>
          <div className="grid grid-cols-3 gap-5">
            <ThresholdCard
              title="Não-balanceada"
              values={form.balancing.unbalanced}
              onChange={(field, value) => handleChange('balancing', 'unbalanced', field, value)}
            />
            <ThresholdCard
              title="Médio-balanceada"
              values={form.balancing.mediumBalanced}
              onChange={(field, value) => handleChange('balancing', 'mediumBalanced', field, value)}
            />
            <ThresholdCard
              title="Balanceada"
              values={form.balancing.balanced}
              onChange={(field, value) => handleChange('balancing', 'balanced', field, value)}
            />
          </div>
        </section>

        {/* Liquidity Thresholds */}
        <section className="flex flex-col gap-4">
          <h2 className="text-base font-semibold text-blueDarker">Limiares da Liquidez</h2>
          <div className="grid grid-cols-3 gap-5">
            <ThresholdCard
              title="Baixa liquidez"
              values={form.liquidity.low}
              onChange={(field, value) => handleChange('liquidity', 'low', field, value)}
            />
            <ThresholdCard
              title="Boa liquidez"
              values={form.liquidity.good}
              onChange={(field, value) => handleChange('liquidity', 'good', field, value)}
            />
            <ThresholdCard
              title="Excesso de liquidez"
              values={form.liquidity.excess}
              onChange={(field, value) => handleChange('liquidity', 'excess', field, value)}
            />
          </div>
        </section>

        {/* Sales Planning - Loss Offset */}
        <section className="flex flex-col gap-4">
          <h2 className="text-base font-semibold text-blueDarker">Planejamento de Vendas — Compensar Prejuízo</h2>
          <div className="grid grid-cols-3 gap-5">
            <div className="bg-blueLight rounded-2xl p-5 flex flex-col gap-4">
              <h3 className="text-base font-semibold text-blueDarker">Margem mínima de prejuízo</h3>
              <CurrencyInput
                name="salesPlanningMinLossMargin"
                value={form.salesPlanningMinLossMargin}
                onChange={(e) => handleSalesPlanningMinLossMarginChange(e.target.value)}
              />
              <p className="text-xs text-blueSteel">
                No modo &ldquo;Compensar Prejuízo&rdquo;, só entram no plano de venda as posições com prejuízo igual
                ou maior que esse valor, para evitar que a venda seja executada já em lucro por oscilação de preço.
              </p>
            </div>
          </div>
        </section>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <CustomButton
            onClick={handleCancel}
            fontSize="text-sm"
            bgColor="bg-blueUltraLight"
            borderColor="border-graySemiDark"
            className="text-grayDark"
            disabled={loading}
          >
            CANCELAR
          </CustomButton>
          <CustomButton onClick={handleSave} fontSize="text-sm" disabled={loading}>
            SALVAR ALTERAÇÕES
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;
